package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.AppearanceAwareImage
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.styles.UnderLineLinkVariant
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.util.JetpackCompose

@Page("/compose/side_effect")
@Composable
fun SideEffectComposePage() {
    BlogLayout(
        PageProperties(
            "Side Effect | Effect APIs in Compose",
            "In Jetpack Compose, Side effects (Effect Handler) are ways to handle changes outside compose the UI hierarchy that need to be reflected in your Compose functions or to ensure certain actions take place as a result of state changes. A side effect in Compose is a change to the state of the app that happens outside the scope of a composable function. Every side effect 3 main key features are Automatic Coroutine Scope (Enter composition, Leave composition), State-Driven (Trigger) and Designed for Side Effects (Perform the outside or inside composable)."
        ),
        BlogProperties("Jetpack Compose", "Side Effect | Effect APIs in Compose")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "When we annotate @Composable we tell the Compose compiler that this function is meant to convert data into UI elements.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "This rendering process happens in three key phases: Restartable, Skippable and Recomposition",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Side effects are operations that escape Compose's declarative model, such as modifying external state (logging, navigation, or launching coroutines).",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "While effect handlers manage when and where asynchronous or lifecycle tasks run, Compose also provides dedicated Side-Effect State APIs. These utilities act as specialized state helpers designed to bridge state changes, optimize recompositions, and eliminate common concurrency bugs like stale data reads.",
                modifier = Modifier.padding(topBottom = 0.3.em).fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Effect-Handlers in Jetpack Compose are mechanisms used to manage side effects in the UI. They facilitate the execution of tasks that are not directly related to rendering the user interface, such as network requests, database operations, or animations. Effect handlers enhance performance, maintainability, and debugging by separating non-UI tasks from UI rendering logic.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Whether you're launching coroutines, preserving state, or responding to key changes, understanding tools like LaunchedEffect, rememberCoroutineScope, and rememberUpdatedState is essential for building effective and responsive apps.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "State Management",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable functions manage state with variables. Use remember to store state across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is state and how does it relate to Jetpack Compose UI?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, state refers to any piece of data that can change over time and has a direct impact on what your UI looks like. If that value changes — whether it’s a simple Int, a String, or a more complex state object like a list or custom data class — Jetpack Compose responds by updating the UI accordingly. That’s the core idea: the UI is a direct reflection of the current state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does state management work in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State management in Jetpack Compose revolves around the concept of a mutable state. Compose provides the mutableStateOf function to create observable state objects. When the state changes, Jetpack Compose automatically recomposes only the affected parts of the UI, ensuring efficient UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "SwiftUl and Jetpack Compose State Management",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SwiftUl provides property wrappers like @State, @Binding, @ObservedObject, and@EnvironmentObject, relying on Combine for reactive updates. Jetpack Compose, on the other hand, integrates seamlessly with ViewModels, LiveData, and StateFlow, making state management more structured and scalable. Compose also offers remember and derivedStateOf for optimized recompositions, whereas SwiftUl's state handling can sometimes lead to unnecessary Ul updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why use States in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "States are a powerful feature of Jetpack Compose that allow developers to create dynamic and interactive UIs with minimal code. By using states, we can update the UI in response to user interactions or changes in data, without having to manage complex UI components manually.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By encapsulating the state of a UI component in a state object, we can clearly see how the UI is being updated and what values are being used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Since states are just variables that can be updated, we can easily test the UI by changing the state and verifying that the UI is updated correctly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State can be managed using the mutableStateOf() function, which returns a MutableState object that can be read and updated using the value property. Alternatively, the state property can be used, which is a convenience wrapper around mutableStateOf().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, state can be managed using the remember function, which creates a variable that survives recomposition, and the mutableStateOf() function, which creates a mutable state object that can be updated. The state can be accessed using the value property or using a property delegate such as var state by remember { mutableStateOf() }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SwiftUI, state can be managed using the @State property wrapper, which creates a state variable that can be updated using the self.propertyName = syntax. The state can be accessed using the value property or by using a property wrapper such as @State private var state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }



            SpanText(
                text = "What is a 𝗠𝘂𝘁𝗮𝗯𝗹𝗲𝗦𝘁𝗮𝘁𝗲𝗙𝗹𝗼𝘄?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ideal for integrating flow data outside of Compose and works seamlessly with reactive structures.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose Runtime Provides Core Functionality",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Observation",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Remember",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Effect Management",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Snapshot System",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When should I use a ViewModel versus remember { mutableStateOf(...) }?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use remember for transient UI state that is local to a single composable or screen, and does not need domain logic (e.g., whether a dialog is open, a text field's current input, a simple counter).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use a ViewModel for screen-level business logic and data that must:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Survive configuration changes (like screen rotation).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Interact with data sources (Repositories, databases, APIs).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Be shared or managed across multiple composables.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "How does state management work in Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose uses reactive state and unidirectional data flow. When state (like mutableStateOf) changes, Compose automatically rebuilds affected composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For complex cases, use:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel for shared business logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow or LiveData to expose state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "collectAsState()  or collectAsStateWithLifecycle() to observe state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://www.linkedin.com/in/vengateshm13/
            SpanText(
                text = "Suspend Functions Reusable in Jetpack Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, we often need to launch suspend functions inside composables, but what if we want a cleaner and reusable way to do it? Instead of launching a coroutine directly inside a composable, we can create a reusable function runAsync() that abstracts coroutine scope management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = " Why use runAsync()?",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoids repetitive coroutine launching code",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Keeps Composable functions clean and modular",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Enhances reusability across multiple use cases",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    @Composable
                        fun runAsync(block: suspend () -> Unit): () -> Unit {
                        val scope = rememberCoroutineScope
                        return { scope.launch { block }}
                    }

                    val scrollState = rememberScrollState()

                    //Onclick
                    onClick = runAsync {
                        scrollState.scrollTo(0)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Effect Handler",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Without them, the same operation could run repeatedly on every recomposition, resources might never be released, and your app could suffer from bugs or performance issues. The effect APIs give us precise control and automatic cleanup.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose uses a unidirectional data flow model to build the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Therefore, updating the UI state is triggered by modifying the state on top of the data flow. However, sometimes there are operations that applications need to perform that may not be directly related to the UI state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, operations like downloading data from an API, reading/writing files, and local database operations fall into this category.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If effect handlers are not used to perform these operations, they can block the UI thread and negatively impact the application’s performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composables enter the composition when attached and leave it when detached. Between both events, effects run. Some effects can outlive this cycle and span across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For instance, a network library like Retrofit or Ktor can be used to make an API request. However, these libraries can block the UI thread and cause the user interface to freeze.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Effect handlers increase the testability of the code, make code maintenance easier, improve the performance of the written code, and make it easier to add new code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.SIDE_EFFECT_EXTERNAL_URL,
                            text = "SideEffect - (Update External Systems After UI Changes - For Running on Every Recomposition - Synchronizing State with External Systems - Sharing State with Non-Compose Code)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.LAUNCHED_EFFECT_URL,
                            text = "LaunchedEffect - (For One-Time Actions with State - Managing Coroutines in Composables - Running Suspend Functions - one time operations or operations triggered by changes key)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.PRODUCE_STATE_URL,
                            text = "produceState - (Callbacks (Location) - Converting External State into Compose State - Bringing Non-Compose State into Compose - Asynchronous to State - Converts suspend functions to State<T>)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.REMEMBER_URL,
                            text = "remember - (Short-Lived UI State - Function memoization)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.REMEMBER_UPDATED_STATE_URL,
                            text = "rememberUpdatedState - (Always Use the Latest Callback - Preventing Unnecessary Effect Restarts - Prevents stale captures in long-lived effects long-running coroutines or callbacks - Non-Restarting References - apture the latest value without restarting an effect)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.REMEMBER_COROUTINE_SCOPE_URL,
                            text = "rememberCoroutineScope - (User Actions - Lifecycle-Aware Coroutines - Trigger coroutines from event handlers -  Launching Coroutines Outside Composition - Lives as long as the composable - Handle User-Initiated Actions - Automatically cancelled when the associated Composable is removed - Event-driven - Event-Based Work)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.SAVEABLE_STATE_HOLDER_URL,
                            text = "SaveableStateHolder",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.REMEMBER_SAVEABLE_URL,
                            text = "rememberSaveable - (Serializable UI State, Survive Rotation, SavedStateRegistry(Bundle, Parcelable, Serializable))",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.REMEMBER_SERIALIZABLE_URL,
                            text = "rememberSerializable",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.SAVER_CUSTOM_URL,
                            text = "Saver Custom",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT),
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.RETAIN_URL,
                            text = "retain - (Persist Objects Across Configuration Changes (But Not Process Death))",
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.SNAPSHOT_FLOW_URL,
                            text = "snapshotFlow (Transform (Bridge) Compose State into Flows - Flow operators (like map, filter, debounce, sorting, pagination (bottom check)))",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.DERIVED_STATE_OF_URL,
                            text = "derivedStateOf - (Efficiently Deriving State from Other State(S) - Optimizing Recomposition, Calculations - Expensive computations, filter, pagination (bottom check), scrolling index (bottom to top, hide/show button), LazyColumn Animation, Validation)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.DISPOSABLE_EFFECT_URL,
                            text = "DisposableEffect - (Handling Resource Cleanup - Lifecycle-Aware Cleanup)",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            variant = UnderLineLinkVariant,
                            path = JetpackCompose.SideEffect.SAVED_STATE_URL,
                            text = "savedstate",
                            modifier = Modifier.margin(right = 4.px).fontSize(FontSize.Medium)
                                .textDecorationLine(TextDecorationLine.None)
                                .color(Resources.Colors.FOOTER_COLOR_LIGHT)
                        )
                        AppearanceAwareImage(
                            src = Resources.Images.NAVIGATION_ARROW
                        )
                    }
                }
            }

            SpanText(
                text = "Why does Column log first?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect → runs first (executed first, immediately after composition): is synchronous and meant to handle setup/cleanup immediately after composition. It's designed for setting up and cleaning up resources that need to be tied to the lifecycle of a composable. For instance, you might use it to register a listener when the composable enters the composition and unregister it when the composable leaves.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect → runs after (Executed after DisposableEffect): starts a coroutine, and coroutine launching is asynchronous, scheduled to run after other synchronous effects like DisposableEffect.  It launches a coroutine to perform suspend functions or other asynchronous operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                   @Composable
                   fun TimerScreen() {
                       val elapsedTime = remember { mutableStateOf(0) }

                       DisposableEffect(Unit) {
                           val scope = CoroutineScope(Dispatchers.Default)
                           val job = scope.launch {
                               while (true) {
                                   delay(1000)
                                   elapsedTime.value += 1
                                   log("Timer is still working {elapsedTime.value}")
                               }
                           }

                           onDispose {
                               job.cancel()
                           }
                       }

                       Text(
                           text = "Elapsed Time: {elapsedTime.value}",
                           modifier = Modifier.padding(16.dp),
                           fontSize = 24.sp
                       )
                   }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun LifecycleAwareComponent() {
                        val lifecycleOwner = LocalLifecycleOwner.current

                    DisposableEffect(lifecycleOwner) {
                            val observer = LifecycleEventObserver { _, event ->
                                when (event) {
                                    Lifecycle.Event.ON_START -> // Handle start
                                    Lifecycle.Event.ON_STOP -> // Handle stop
                                }
                            }
                            lifecycleOwner.lifecycle.addObserver(observer)
                            onDispose {
                                lifecycleOwner.lifecycle.removeObserver(observer)
                            }
                        }
                        Text("Lifecycle aware component")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun LocationUpdatesScreen(context: Context) {
                        var location by remember { mutableStateOf<Location?>(null) }

                        DisposableEffect(Unit) {
                            val locationListener = LocationListener { loc ->
                                location = loc
                            }
                            val locationManager = context.getSystemService(Context.LOCATION_SERVICE) as LocationManager

                            locationManager.requestLocationUpdates(LocationManager.GPS_PROVIDER, 1000L, 1f, locationListener)

                            onDispose {
                                locationManager.removeUpdates(locationListener)
                            }
                        }

                        location?.let {
                            Text("Updated Location")
                        } ?: CircularProgressIndicator()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun LocationTracker() {
                        DisposableEffect(Unit) {
                            // Start tracking location when composable enters composition
                            startLocationUpdates()

                            onDispose {
                                // Stop tracking when composable leaves composition
                                stopLocationUpdates()
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Five main side-effect handlers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect: Launches coroutines for operations that should run on composable entry or when a key changes. Need to run a suspend function when the composable loads or when specific values change?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope: Provides a coroutine scope for UI interactions that need to run in a coroutine. Need to handle user interactions that require coroutines?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect: Triggers simple side effects during recomposition (e.g., logging or UI updates). Need to update non-Compose code to stay in sync with Compose state?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberUpdatedState: Have a long-running effect that uses callbacks, and the callbacks might change?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect: Handles resource cleanup when composables leave the composition (DisposableEffect is ideal for managing resources that need explicit cleanup, such as registering listeners.).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow: Allows you to observe state changes with a flow and react to them in a structured manner.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ProduceState: ProduceState creates a State from a coroutine and is handy for managing data sources. Need to convert async data into Compose state with loading/error handling?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Summary",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect is executed when its parent Composable is recomposed and is useful for executing operations that do not depend on the Composable's state or props. Triggers synchronous side effects in response to composition changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect is executed when its parent Composable is first rendered and is useful for managing resources that need to be cleaned up when a Composable is no longer in use. It triggers on first composition or key change and it invokes onDispose method on termination.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect executes a side effect in a separate coroutine scope, and is useful for executing long-running operations without blocking the UI thread. It triggers on first composition or key change. Executes side effects asynchronously in Jetpack Compose, triggered by changes in state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope in Jetpack Compose provides a coroutine scope that survives configuration changes, allowing for the execution of coroutines within a composable function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberUpdatedState in Jetpack Compose is a utility function used to create a state that updates only when its value changes, optimizing recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState in Jetpack Compose is a function used to create a state that produces a stream of values asynchronously, updating the UI whenever a new value is emitted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf in Jetpack Compose is a function used to create a state that depends on other state values, triggering recomposition only when the derived state or any of its dependencies change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow in Jetpack Compose is a function used to create a flow that emits snapshots of a state at the time of collection, ensuring consistent data retrieval.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does MutableState work in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableState is an observable data holder that allows composables to react to changes in state automatically. When the value of a MutableState object changes, Jetpack Compose triggers a recomposition for any composables that read that state, updating the UI to reflect the new data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableState is typically created using the mutableStateOf function. This function returns an instance of MutableState that holds the initial value and updates the value whenever it changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember Vs mutableState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If remember is used with a field its value will persist across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If mutableState is used with a field, all the composables which are using that field will be recomposed whenever the field values change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The remember keyword can store a mutable or immutable object. As seen in the example above, we can store the state of the ‘textValue’ variable we created in memory with the remember keyword. In this way, when the value of our textValue variable changes, all view elements that are affected will be updated and redrawn on the screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If we want state changes to be observed by Compose, we should use MutableState or collectAsState() constructs. We can declare a MutableState object in Composable in three different ways.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use to store an object in memory.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use to create a mutable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Value is kept across recompisitions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates an observable state holder.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lost when activity is recreated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Read and Write the value with state.value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "LaunchedEffect() Vs SideEffect()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The LaunchedEffect coroutine is not initialised during the composition phase, but after the commit phase, i.e. after the recomposition has succeeded.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "So if you update a state in a LaunchedEffect immediately, you can expect that this change will take effect in the next frame, not this recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LaunchedEffect runs by starting the coroutine in the frame after the recomposition is completed - can be asynchronous and delayed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SideEffect runs immediately after each successful recomposition, before the UI is updated - synchronous and fast.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "LaunchedEffect() Vs rememberCoroutineScope()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launch effect/coroutine from within a composable?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Yes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "No",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launch effect/coroutine outside a composable? E.g. from the callback",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "No",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Yes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When effect/coroutine is started?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LaunchedEffect() enters composition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineScope.launch() is called",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When effect/coroutine is canceled?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LaunchedEffect() leaves composition, LaunchedEffect() is restarted (effect's key changed)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineScope leaves composition. Note: When coroutine is restarted, the previous coroutine will NOT be canceled",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When effect/coroutine is restarted?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LaunchedEffect()'s key changed (while it is still in composition)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineScope.launch() is called again",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "DisposableEffect() Vs SideEffect() - Non-suspended Effect Handler",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launch effect from within a composable?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Yes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Yes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launch effect outside a composable? E.g. from the callback",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "No",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "No",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When effect is started?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DisposableEffect() enters composition, after the current composition completes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SideEffect() enters composition, after the current composition completes",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When effect is canceled?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Effect can NOT be canceled, since the execution can NOT be suspended(non-suspended function)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Effect can NOT be canceled - same as DisposableEffect()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When effect is restarted?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DisposableEffect()'s key changed (while it is still in composition)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SideEffect() enters recomposition - every recomposition triggers the SideEffect() to run",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When onDispose() is called?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DisposableEffect() leaves composition, DisposableEffect() is restarted (effect's key changed) Note: When the effect completes, it will NOT trigger onDispose()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "N/A",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "DisposableEffect() Vs LaunchedEffect()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect is intended for suspendable tasks that can be canceled if the key changes or the composable leaves the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect is ideal for managing non-suspendable side effects (like attaching listeners or managing resources).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use `LaunchedEffect` when you need to handle asynchronous tasks ( LaunchedEffect is mainly used to start a coroutine to perform asynchronous operations when composables are combined, and it focuses on the execution of asynchronous tasks); choose `DisposableEffect` when you need reliable setup and teardown of side effects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember() Vs rememberSaveable()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember: remember is used to retain state only during the current composition. Once the composable is recomposed due to configuration changes (like screen rotation), the value is lost. Stores state for the current composition only; not retained after configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable: on the other hand, retains the state across configuration changes by saving the value in a Bundle. It uses SavedStateRegistry under the hood. Saves state in a Bundle; survives configuration changes and process recreation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember() Vs rememberSaveable()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember: This function is used to store a value that should be remembered across recompositions of a composable function. It's similar to using a variable outside of the composable, but it ensures that the Value is only recomputed when its dependencies change. This function is used to store a value that should be remembered across recompositions of a composable function. It's similar to using a variable outside of the composable, but it ensures that the Value is only recomputed when its dependencies change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable: rememberSavable {} will survive changes, and keep its value. This function is used for Saving and restoring a value across configuration changes, such as screen rotations. It's particularly useful when you want to retain a piece of state across different instances of a composable after the screen configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember() Vs rememberSaveable()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "remember caches objects locally within the in-memory Slot Table (wiped during configuration changes).",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "remember captures the initial value and won't update unless the key changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Keeps state only while the composable is active in the composition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Loses state when the activity is recreated (e.g., after screen rotation).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Best for temporary Ul states, like toggling visibility within a single session.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "rememberSaveable serializes state into an OS Bundle to survive process death and activity recreation.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If your lambda or value changes frequently, use rememberUpdatedState () to avoid extra recompositions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Similar to remember but can survive configuration changes like rotations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Automatically saves data in a Bundle, maintaining state even if the activity is recreated.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ideal for retaining user input, scroll positions, or any Ul data that users shouldn't have to re-enter after a rotation.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "SavedStateHandle and rememberSaveable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SavedStateHandle: Use for Business/Presentation State (IDs, search queries) in the ViewModel. It is the most reliable anchor for process restoration.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable: Use for Pure UI State (scroll position, animation toggles) in the Composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "How to handle process death?",
                    modifier = Modifier.fontSize(FontSize.Large)
                        .fontWeight(FontWeight.Bold)
                )
            }

            KotlinCode(
                code = """
                        // 1. rememberSaveable for Compose UI state
                        var text by rememberSaveable { mutableStateOf("") }

                        // 2. SavedStateHandle in ViewModel
                        class MyViewModel(private val savedState: SavedStateHandle) : ViewModel() {
                            val searchQuery = savedState.getStateFlow("query", "")
                            fun updateQuery(q: String) { savedState["query"] = q }
                        }
                        
                        // 3. Room for persistent data (survives app kill)
                        
                        // 4. DataStore for user preferences
                    """.trimIndent()
            )

            SpanText(
                text = "remember and rememberUpdatedState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the key changes, Compose cancels the previous effect — even if the composable itself is still present — and then starts a new one. That means onDispose runs every time the key changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        @Composable
                        fun ProfileScreen(userId: String) {
                            DisposableEffect(userId) {
                                println("Effect started")

                                onDispose {
                                    println("Effect cleaned up")
                                }
                            }

                            // UI content
                        }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "What is recomposition in Jetpack Compose ? How does Compose optimize UI updates?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition is the process by which Jetpack Compose re-runs composable functions to update the UI in response to state changes. When the state changes, Compose re-executes only the affected composable(s).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does state management work in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State management in Jetpack Compose revolves around the concept of a mutable state. Compose provides the mutableStateOf function to create observable state objects. When the state changes, Jetpack Compose automatically recomposes only the affected parts of the UI, ensuring efficient UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the state changes, Jetpack Compose automatically recomposes only the affected parts of the UI, ensuring efficient UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutuableState: State management in Jetpack Compose revolves around the concept of a mutable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutuableStateOf: Compose provides the mutableStateOf function to create observable state objects. Observe state changes and trigger recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hosting: State is passed as parameter to a composables and modified outside them for better reusability and testability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hosting: State is passed as parameter to a composables and modified outside them for better reusability and testability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "derivedStateOf Vs mutableStateOf",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimizes recompositions by caching computed values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use it when you need to derive a new state from existing states.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Updates only when the input states change, minimizing unnecessary recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Directly stores mutable state values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Triggers recomposition whenever the state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ideal for managing simple, direct state without derivation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How do you handle side effects inside composable functions?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose provides several side-effect handler APIs that allow you to handle operations outside of the composable’s recomposition scope. These APIs are essential for dealing with scenarios like interacting with the Android framework, managing composition events, or triggering effects based on state changes. Let’s explore the three primary side-effect handler APIs: LaunchedEffect, DisposableEffect, and SideEffect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Do all the Side Effects Run after composition?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect: Runs after composition but before rendering.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect: Runs on every recomposition to trigger UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DisposableEffect: Runs after composition and cleans up on disposal.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ProduceState: Runs after composition but manages a state producer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "snapshotFlow: Captures state snapshots and flows after composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    fun fetchData(): Int {
                        return 42 // Simulated non-Compose state
                    }

                    @Composable
                    fun ConvertNonComposeState() {
                        val state = remember { mutableStateOf(fetchData()) }
                        Text("Data: ${state.value}")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ObserveCallbackState(callback: () -> String) {
                        var state by remember { mutableStateOf(callback()) }
                        Button(onClick = { state = callback() }) {
                            Text(text = state)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun StateDemoScreen() {
                        var r1 by remember { mutableStateOf(0) }
                        var r2 by rememberSaveable { mutableStateOf(0) }
                        val nonSerializableCounter = retain { NonSerializableCounter() }

                        Column {
                            Text("remember: $r1")
                            Text("rememberSaveable: $r2")
                            Text("retain: ${nonSerializableCounter.count}")

                            Button(onClick = { r1++ }) { Text("Increment remember") }
                            Button(onClick = { r2++ }) { Text("Increment saveable") }
                            Button(onClick = { nonSerializableCounter.increment() }) {
                                Text("Increment retain")
                            }
                        }
                    }

                    class NonSerializableCounter {
                        var count = 0
                        fun increment() { count++ }
                    }

                    """.trimIndent()
            )

            /*
            | Action                   | remember | rememberSaveable | retain   |
            | ------------------------ | -------- | ---------------- | -------- |
            | Recompose                | persists | persists         | persists |
            | Rotate device            | resets   | persists         | persists |
            | Kill process             | resets   | persists         | resets   |
            | Supports complex objects | ❌       | ❌               | ✅       |
             */

            SpanText(
                text = "How do you avoid unnecessary recompositions in a large Compose layout?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use remember to cache computations or values that don’t need to be recomputed on every recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Make composables stateless when possible — state hoisting ensures changes only recompose the minimum necessary UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Apply derivedStateOf to compute derived values only when dependencies change, which prevents costly re-computation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoid passing changing lambdas or objects as parameters unless memoized with remember or rememberUpdatedState, because Compose will see them as new each time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Break down large composable into smaller, focused ones so only the affected part of the UI is recomposed when state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use LazyColumn, LazyRow, or LazyGrid for large lists — these only compose visible items.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "I use tools like Modifier.debugInspectorInfo and Compose compiler metrics to profile and verify recomposition behaviour during development.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/Rohit-554/CMP-TODO-LIST.git
            KotlinCode(
                code = """
                    //SnackBar
                    
                    expect val screenDensity: Float
                    expect val screenWidth: Dp
                    expect val screenHeight: Dp
                    
                    //iosMain
                    import androidx.compose.ui.unit.Dp
                    import androidx.compose.ui.unit.dp
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents
                    import platform.UIKit.UIScreen

                    actual val screenDensity: Float
                        get() = UIScreen.mainScreen.scale.toFloat()

                    @OptIn(ExperimentalForeignApi::class)
                    actual val screenWidth: Dp
                        get() = UIScreen.mainScreen.bounds.useContents { size.width }.dp

                    @OptIn(ExperimentalForeignApi::class)
                    actual val screenHeight: Dp
                        get() = UIScreen.mainScreen.bounds.useContents { size.height }.dp
                        
                    //androidMain    
                    import android.content.Resources.Resources
                    import androidx.compose.ui.unit.Dp
                    import androidx.compose.ui.unit.dp

                    actual val screenDensity: Float
                        get() = Resources.getSystem().displayMetrics.density

                    actual val screenWidth: Dp
                        get() {
                            val pixels = Resources.getSystem().displayMetrics.widthPixels.toFloat()
                            return (pixels / screenDensity).dp
                        }

                    actual val screenHeight: Dp
                        get() {
                            val pixels = Resources.getSystem().displayMetrics.heightPixels.toFloat()
                            return (pixels / screenDensity).dp
                    }
                    
                    object AppSnackBarHost {
                        val snackBarHostState = SnackbarHostState()
                    }

                    @OptIn(DelicateCoroutinesApi::class)
                    fun showSnackBar(
                        message: String,
                        duration: SnackbarDuration = SnackbarDuration.Short,
                        positiveMessage: Boolean = false,
                        actionLabel: String? = null,
                        onAction: (() -> Unit)? = null
                    ) {
                        var actionLabelLocal = actionLabel
                        if (actionLabel == null && positiveMessage)
                            actionLabelLocal = "POSITIVE"
                        else if (actionLabel == null && !positiveMessage)
                            actionLabelLocal = "NEGATIVE"

                        GlobalScope.launch {
                            AppSnackBarHost.snackBarHostState.showSnackbar(
                                message,
                                duration = duration,
                                actionLabel = actionLabelLocal
                            )
                        }
                    }

                    @Composable
                    fun CustomSnackBarHost(
                        snackBarHostState: SnackbarHostState = AppSnackBarHost.snackBarHostState,
                        modifier: Modifier = Modifier
                    ) {
                        var showAnimation by remember { mutableStateOf(false) }
                        var currentMessage by remember { mutableStateOf("") }
                        var isSuccess by remember { mutableStateOf(true) }
                        var animationDuration by remember { mutableStateOf(800L) }
                        LaunchedEffect(snackBarHostState.currentSnackbarData) {
                            snackBarHostState.currentSnackbarData?.let { data ->
                                val actionLabel = data.visuals.actionLabel

                                animationDuration = when (data.visuals.duration) {
                                    SnackbarDuration.Short -> 800L
                                    SnackbarDuration.Long -> 2000L
                                    SnackbarDuration.Indefinite -> 4000L
                                }
                                when (actionLabel) {
                                    "POSITIVE" -> {
                                        isSuccess = true
                                        currentMessage = data.visuals.message
                                        showAnimation = true
                                    }

                                    "NEGATIVE" -> {
                                        isSuccess = false
                                        currentMessage = data.visuals.message
                                        showAnimation = true
                                    }
                                }
                            }
                        }

                        BarfiSnackBar(
                            message = currentMessage,
                            isSuccess = isSuccess,
                            isVisible = showAnimation,
                            onAnimationComplete = {
                                showAnimation = false
                                snackBarHostState.currentSnackbarData?.dismiss()
                            },
                            onDismiss = {
                                showAnimation = false
                                snackBarHostState.currentSnackbarData?.dismiss()
                            },
                            modifier = modifier,
                            displayDurationMillis = animationDuration
                        )

                    }

                    @Composable
                    fun BarfiSnackBar(
                        message: String = "Success",
                        isSuccess: Boolean = true,
                        isVisible: Boolean = true,
                        onAnimationComplete: (() -> Unit)? = null,
                        onDismiss: (() -> Unit)? = null,
                        modifier: Modifier = Modifier,
                        displayDurationMillis: Long = 800L
                    ) {

                        val circleOffsetX = remember { Animatable(0f) }
                        val offsetY = remember { Animatable(-400f) }
                        val offsetXDrag = remember { Animatable(0f) }
                        val rectangleWidth = remember { Animatable(0f) }
                        val circleScale = remember { Animatable(1f) }
                        val rectangleAlpha = remember { Animatable(0f) }
                        val glowIntensity = remember { Animatable(0f) }
                        val checkmarkProgress = remember { Animatable(0f) }
                        val rippleScale = remember { Animatable(0f) }
                        val shadowOffset = remember { Animatable(0f) }
                        val textAlpha = remember { Animatable(0f) }
                        val coroutineScope = rememberCoroutineScope()
                        val screenWidth = screenWidth.value
                        val circleDistance = -screenWidth * 0.35f
                        val customOvershoot = CubicBezierEasing(0.25f, 0.46f, 0.45f, 0.94f)

                        // Colors based on success/error
                        val primaryColor = if (isSuccess) Color(0xFF4CAF50) else Color(0xFFE53E3E)
                        val gradientColors = if (isSuccess) {
                            listOf(Color(0xFF81C784), Color(0xFF4CAF50), Color(0xFF66BB6A))
                        } else {
                            listOf(Color(0xFFFF8A80), Color(0xFFE53E3E), Color(0xFFEF5350))
                        }

                        LaunchedEffect(isVisible) {
                            if (isVisible) {
                                // Reset all values
                                circleOffsetX.snapTo(0f)
                                offsetY.snapTo(-400f)
                                offsetXDrag.snapTo(0f)
                                rectangleWidth.snapTo(0f)
                                circleScale.snapTo(1f)
                                rectangleAlpha.snapTo(0f)
                                glowIntensity.snapTo(0f)
                                checkmarkProgress.snapTo(0f)
                                rippleScale.snapTo(0f)
                                shadowOffset.snapTo(0f)

                                // Animation sequence
                                launch { rectangleAlpha.animateTo(0.3f, tween(200)) }
                                launch { glowIntensity.animateTo(1f, tween(200)) }
                                launch { shadowOffset.animateTo(8f, tween(150)) }
                                checkmarkProgress.animateTo(1f, tween(80, easing = FastOutLinearInEasing))
                                offsetY.animateTo(0f, spring(dampingRatio = 0.7f, stiffness = 400f))

                                delay(200)

                                launch {
                                    circleScale.animateTo(1.1f, tween(80, easing = FastOutSlowInEasing))
                                    delay(40)
                                    circleScale.animateTo(1f, tween(120, easing = LinearOutSlowInEasing))
                                }

                                launch {
                                    rectangleWidth.animateTo(
                                        targetValue = screenWidth*0.8f,
                                        animationSpec = spring(dampingRatio = 0.8f, stiffness = 300f)
                                    )
                                    textAlpha.animateTo(
                                        targetValue = 1f,
                                        animationSpec = tween(durationMillis = 300)
                                    )
                                }


                                launch {
                                    rectangleAlpha.animateTo(1f, tween(400, easing = FastOutSlowInEasing))
                                }

                                circleOffsetX.animateTo(circleDistance, spring(dampingRatio = 0.9f, stiffness = 350f))

                                delay(600)

                                launch {
                                    rippleScale.animateTo(2f, tween(400, easing = LinearOutSlowInEasing))
                                    rippleScale.animateTo(0f, tween(80))
                                }

                                delay(displayDurationMillis)

                                launch {
                                    textAlpha.animateTo(
                                        targetValue = 0f,
                                        animationSpec = tween(durationMillis = 200)
                                    )
                                }
                                delay(200)

                                launch {
                                    circleScale.animateTo(0.9f, tween(80))
                                    circleScale.animateTo(1f, tween(150, easing = customOvershoot))
                                }

                                launch {
                                    rectangleWidth.animateTo(0f, spring(dampingRatio = 0.9f, stiffness = 300f))
                                }
                                delay(25)
                                circleOffsetX.animateTo(0f, spring(dampingRatio = 0.8f, stiffness = 400f))


                                launch {
                                    rectangleAlpha.animateTo(0f, tween(200))
                                    glowIntensity.animateTo(0f, tween(200))
                                }

                                checkmarkProgress.animateTo(0f, tween(150))

                                launch {
                                    circleScale.animateTo(0.8f, tween(120))
                                    shadowOffset.animateTo(0f, tween(120))
                                }
                                offsetY.animateTo(-400f, spring(dampingRatio = 1.0f, stiffness = 500f))

                                delay(300)
                                onAnimationComplete?.invoke()
                            }
                        }

                        if (isVisible) {
                            Box(
                                modifier = modifier
                                    .fillMaxWidth()
                                    .offset {
                                        IntOffset(
                                            offsetXDrag.value.roundToInt(),
                                            offsetY.value.roundToInt()
                                        )
                                    }
                                    .pointerInput(Unit) {
                                        detectDragGestures(
                                            onDragEnd = {
                                                coroutineScope.launch {
                                                    if (abs(offsetXDrag.value) > 200f) {
                                                        // Dismiss with slide animation
                                                        val targetX =
                                                            if (offsetXDrag.value > 0) size.width.toFloat() else -size.width.toFloat()
                                                        offsetXDrag.animateTo(
                                                            targetX,
                                                            tween(300, easing = FastOutSlowInEasing)
                                                        )
                                                        onDismiss?.invoke()
                                                    } else {
                                                        // Snap back to center
                                                        offsetXDrag.animateTo(
                                                            0f,
                                                            spring(dampingRatio = 0.7f, stiffness = 400f)
                                                        )
                                                    }
                                                }
                                            }
                                        ) { _, dragAmount ->
                                            coroutineScope.launch {
                                                offsetXDrag.snapTo(offsetXDrag.value + dragAmount.x)
                                            }
                                        }
                                    },
                                contentAlignment = Alignment.TopCenter
                            ) {
                                Box(
                                    modifier = Modifier
                                        .height(60.dp)
                                        .fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    // Ripple effect
                                    if (rippleScale.value > 0f) {
                                        Box(
                                            modifier = Modifier
                                                .size((60 * rippleScale.value).dp)
                                                .clip(CircleShape)
                                                .background(
                                                    primaryColor.copy(
                                                        alpha = (1f - rippleScale.value / 2f).coerceAtLeast(0f)
                                                    )
                                                )
                                        )
                                    }

                                    // Rectangle with text
                                    Box(
                                        modifier = Modifier
                                            .width(rectangleWidth.value.dp)
                                            .offset { IntOffset(x = 2.dp.roundToPx(), y = 6.dp.roundToPx()) }
                                            .height(48.dp)
                                            .shadow(
                                                elevation = 8.dp,
                                                shape = RoundedCornerShape(Spacing.s10),
                                                clip = true
                                            )
                                            .clip(RoundedCornerShape(Spacing.s10))
                                            .background(
                                                Brush.horizontalGradient(
                                                    colors = gradientColors.map { it.copy(alpha = rectangleAlpha.value) }
                                                )
                                            )
                                    ) {
                                        Text(
                                            message,
                                            style = BodyNormal(),
                                            color = Color.White,
                                            modifier = Modifier
                                                .align(Alignment.CenterStart)
                                                .padding(start = 55.dp, end = 8.dp)
                                                .alpha(textAlpha.value)
                                        )
                                    }


                                    // White circle with icon
                                    Box(
                                        modifier = Modifier
                                            .scale(circleScale.value)
                                            .size(60.dp)
                                            .offset(
                                                x = circleOffsetX.value.dp,
                                                y = shadowOffset.value.dp / 2
                                            )
                                            .zIndex(1f)
                                            .shadow(
                                                elevation = 8.dp,
                                                shape = CircleShape,
                                                clip = true
                                            )
                                            .clip(CircleShape)
                                            .background(
                                                Brush.radialGradient(
                                                    colors = listOf(Color.White, Color(0xFFF5F5F5))
                                                )
                                            ),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        if (checkmarkProgress.value > 0f) {
                                            Canvas(modifier = Modifier.size(24.dp)) {
                                                val path = Path()
                                                val progress = checkmarkProgress.value
                                                val strokeWidth = 3.dp.toPx()

                                                if (isSuccess) {
                                                    // Checkmark
                                                    path.moveTo(size.width * 0.2f, size.height * 0.5f)
                                                    if (progress > 0.5f) {
                                                        path.lineTo(size.width * 0.45f, size.height * 0.7f)
                                                    } else {
                                                        path.lineTo(
                                                            size.width * (0.2f + (progress * 0.5f)),
                                                            size.height * (0.5f + (progress * 0.4f))
                                                        )
                                                    }

                                                    if (progress > 0.5f) {
                                                        val secondProgress = (progress - 0.5f) * 2f
                                                        path.lineTo(
                                                            size.width * (0.45f + (secondProgress * 0.35f)),
                                                            size.height * (0.7f - (secondProgress * 0.4f))
                                                        )
                                                    }
                                                } else {
                                                    // X mark
                                                    val currentProgress = progress.coerceAtMost(1f)
                                                    // First line
                                                    path.moveTo(size.width * 0.25f, size.height * 0.25f)
                                                    path.lineTo(
                                                        size.width * (0.25f + (currentProgress * 0.5f)),
                                                        size.height * (0.25f + (currentProgress * 0.5f))
                                                    )
                                                    // Second line
                                                    path.moveTo(size.width * 0.75f, size.height * 0.25f)
                                                    path.lineTo(
                                                        size.width * (0.75f - (currentProgress * 0.5f)),
                                                        size.height * (0.25f + (currentProgress * 0.5f))
                                                    )
                                                }

                                                drawPath(
                                                    path = path,
                                                    color = primaryColor,
                                                    style = Stroke(
                                                        width = strokeWidth,
                                                        cap = StrokeCap.Round,
                                                        join = StrokeJoin.Round
                                                    )
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    
                    Surface(
                        modifier = Modifier.navigationBarsPadding(),
                    ) {
                        Box(modifier = Modifier.fillMaxSize()) {
                            CustomSnackBarHost(
                                modifier =
                                    Modifier
                                        .align(Alignment.TopCenter)
                                        .padding(top = 48.dp)
                                        .zIndex(99999f)
                            )
                            NavHost(
                                navController = navController,
                                startDestination = NavRoute.Home,
                                modifier = Modifier.fillMaxSize()
                            ) { }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/longdt57/Kotlin-Multiplatform.git
            KotlinCode(
                code = """
                    @Suppress("ComposableNaming")
                    @Composable
                    fun <T> Flow<T>.collectAsEffect(
                        block: suspend (T) -> Unit,
                    ) {
                        val lifecycleOwner = LocalLifecycleOwner.current
                        LaunchedEffect(this, lifecycleOwner) {
                            lifecycleOwner.repeatOnLifecycle(Lifecycle.State.STARTED) {
                                withContext(Dispatchers.Main.immediate) {
                                    collect(block)
                                }
                            }
                        }
                    }
                    
                    @Suppress("PropertyName", "MemberVisibilityCanBePrivate")
                    abstract class BaseViewModel : ViewModel() {
                        protected val _navigator = MutableSharedFlow<Any>(
                            extraBufferCapacity = 1,
                            onBufferOverflow = BufferOverflow.DROP_OLDEST
                        )
                        val navigator = _navigator.asSharedFlow()
                    }
                    
                    @Composable
                    internal fun GitUserDetailScreen(
                        navHostController: NavHostController,
                        modifier: Modifier = Modifier,
                    ) {
                        viewModel.navigator.collectAsEffect { destination -> navHostController.navigate(destination) }
                    }
                    """.trimIndent()
            )

            //https://github.com/wshtaits/GetLike.git
            KotlinCode(
                code = """
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.MutableIntState
                    import androidx.compose.runtime.MutableState
                    import androidx.compose.runtime.State
                    import androidx.compose.runtime.derivedStateOf
                    import androidx.compose.runtime.mutableIntStateOf
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.saveable.rememberSaveable
                    
                    @Composable
                    fun <T> rememberMutableStateOf(value: T): MutableState<T> =
                        remember { mutableStateOf(value) }

                    @Composable
                    fun <T> rememberSaveableMutableStateOf(value: T): MutableState<T> =
                        rememberSaveable { mutableStateOf(value) }

                    @Composable
                    fun rememberMutableIntStateOf(value: Int): MutableIntState =
                        remember { mutableIntStateOf(value) }

                    @Composable
                    fun <T> rememberDerivedStateOf(calculation: () -> T): State<T> =
                        remember { derivedStateOf(calculation) }

                    @Composable
                    fun <T> rememberDerivedStateOf(
                        key1: Any?,
                        key2: Any?,
                        calculation: () -> T,
                    ): State<T> =
                        remember(key1, key2) { derivedStateOf(calculation) }
                        
                    //var isRealButtonVisible by rememberMutableStateOf(true)
                    //var likeZoneProgress by rememberMutableStateOf<Float?>(null)    
                """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Visible item update timer 
                    
                    class TimerListViewModel : ViewModel() {
                        private val _timers = MutableStateFlow(List(100) { 0 })
                        val timers: StateFlow<List<Int>> = _timers.asStateFlow()

                        private val visibleItems = mutableSetOf<Int>()

                        init {
                            startTicking()
                        }

                        fun updateVisibleItems(indexes: List<Int>) {
                            visibleItems.clear()
                            visibleItems.addAll(indexes)
                        }

                        private fun startTicking() {
                            viewModelScope.launch {
                                while (isActive) {
                                    if (visibleItems.isNotEmpty()) {
                                        _timers.update { current ->
                                            current.mapIndexed { index, value ->
                                                if (visibleItems.contains(index)) value + 1 else value
                                            }
                                        }
                                    }
                                    delay(1000)
                                }
                            }
                        }
                    }
                    
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContent {
                                PersistentIntuitASCodingTheme {
                                    Scaffold(modifier = Modifier.fillMaxSize()) { innerPadding ->
                                        TimeList(
                                            modifier = Modifier.padding(innerPadding)
                                        )
                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    fun TimeList(modifier: Modifier = Modifier) {
                        val viewModel: TimerListViewModel = viewModel()
                        val timers by viewModel.timers.collectAsState()
                        val listState = rememberLazyListState()

                        // Detect visible items and update timers
                        LaunchedEffect(listState) {
                            snapshotFlow { listState.layoutInfo.visibleItemsInfo.map { it.index } }
                                .collect { visibleIndexes ->
                                    viewModel.updateVisibleItems(visibleIndexes)
                                }
                        }

                        LazyColumn(state = listState) {
                            itemsIndexed(timers) { index, timerValue ->
                                TimerListItem(index, timerValue)
                            }
                        }
                    }

                    @Composable
                    fun TimerListItem(index: Int, timer: Int) {
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .padding(16.dp),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(text = "Item $index")
                            Text(text = "$timer s")
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://www.linkedin.com/pulse/mvvm-clean-architecture-inandroid-anand-gaur-w20pc/
//https://medium.com/@sandeepkella23/understanding-remember-in-jetpack-compose-a-deep-dive-from-first-principles-2587b2098323
//https://www.linkedin.com/pulse/understanding-side-effects-jetpack-compose-guide-gaurav-chauhan-pkrsc/?trackingId=u%2By1xZiaTmulE3wM8isyng%3D%3D
//https://medium.com/@manuaravindpta/effect-handlers-in-jetpack-compose-440ac9bd9f1e
//https://kotlinconf.com/talks/
//https://medium.com/@sahilthakar10/flatmapconcat-flatmapmerge-and-flatmaplatest-internal-working-de1a7c3e0c63
//https://medium.com/@anitaa_1990/kotlin-coroutines-fundamentals-ca25e6c5ffa6
//https://medium.com/@ramadan123sayed/understanding-and-handling-side-effects-in-jetpack-compose-a-comprehensive-guide-85b219e495f1
//https://medium.com/@husayn.fakher/10-questions-about-side-effects-in-jetpack-compose-269f0066fae2
//https://medium.com/@sandeepkella23/understanding-remember-in-jetpack-compose-a-deep-dive-from-first-principles-2587b2098323
//https://medium.com/@kemal_codes/understanding-ref-in-jetpack-compose-ad8f68641215 (Ref use)

/*
https://newsletter.jorgecastillo.dev/p/jetpack-compose-effect-handlers

LaunchedEffect is for suspend functions and coroutines, while SideEffect is for immediate, non-suspend operations that need to happen after every recomposition.

Layout Inspector
Android Studio Layout Inspector is a tool that Android application developers can use. This tool helps them visually inspect and debug the user interfaces of Android applications. Layout Inspector helps developers understand their applications' user interfaces and identify problems.

✨ 𝗛𝗼𝘄 𝘀𝘁𝗮𝘁𝗲 𝗮𝗰𝘁𝘂𝗮𝗹𝗹𝘆 𝘄𝗼𝗿𝗸𝘀
✔ 𝗺𝘂𝘁𝗮𝗯𝗹𝗲𝗦𝘁𝗮𝘁𝗲𝗢𝗳 → creates an observable MutableState<T>
✔ 𝗿𝗲𝗺𝗲𝗺𝗯𝗲𝗿 → stores objects in the Composition during initial composition
✔ 𝗿𝗲𝗺𝗲𝗺𝗯𝗲𝗿𝗦𝗮𝘃𝗲𝗮𝗯𝗹𝗲 → stores in saved instance state (survives process death)

How to work with GC in Compose:
• Use remember for objects with lifecycle
• Prefer immutable + stable data
• Avoid creating collections inside composables
• Hoist state, reuse models
• Think in terms of object lifetime, not just logic

Use remember for short-lived UI state
Use rememberSaveable when the state must survive process death
Use rememberSerializable for structured objects that can be serialized
Use retain for values that are expensive to recreate, cannot be serialized, must survive configuration changes

Key features of Layout Inspector are:
Interface Inspector: Layout Inspector visually displays the interface of a running Android application. Developers can examine every component of the application's user interface (e.g. buttons, text areas, view groups).
Development and Debugging: App developers can understand how an app's UI is laid out using Layout Inspector. This can help them detect problems with proper placement, sizing, and styling.
Properties and Values: Layout Inspector displays the properties and values of each component. This is useful for understanding what properties components have and how these properties are set.
Element Tree: The Layout Inspector displays the user interface of an application in a hierarchical manner. This is useful for understanding how the application's interface is built and how elements relate to each other.
*/
//https://medium.com/@paritasampa95/retain-in-jetpack-compose-lifecycle-beyond-composition-338c756ed553
//https://www.droidunplugged.com/2026/01/cracking-android-sde2sde3-interviews-in_17.html

//https://medium.com/@abdullahkhan1571998/understanding-launchedeffect-in-jetpack-compose-a-complete-guide-6721b9935fc6

//https://www.linkedin.com/video/live/urn:li:ugcPost:7429495487428423680/?originTrackingId=MBlWEOdXbCWxleD8yq82kA%3D%3D
//https://multithreaded.stitchfix.com/blog/2022/08/05/jetpack-compose-recomposition/
//https://medium.com/@ahmed.ally2/rememberserializable-vs-remembersaveable-367bd0761767
//https://medium.com/@chanzmao/mastering-compose-with-launchedeffect-and-producestate-93be7b194c51
//https://blog.shreyaspatil.dev/deep-dive-into-annotations-in-jetpack-compose
//https://medium.com/@arungohil10.ag/mastering-jetpack-compose-side-effect-states-7c5c71e56526